import { IconCaretDownFilled, IconCaretRightFilled, IconCaretUpFilled } from "@tabler/icons-react"
import { Link, LinkProps } from "@tanstack/react-router"
import { formatPercent, isNil } from "lopermut"
import { useMemo } from "react"
import { Skeleton } from "../../atoms/Skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { Card } from "../../molecules/Card"
import { cn } from "../../utils"

type OnClickOrLinkOrNothing =
  | {
      onClick?: () => void
    }
  | {
      navigate: LinkProps
    }
  | never

type Props = {
  title: string
  currentValue: string
  deltaPercentage?: number
  deltaTooltip?: string
  metric?: string | React.ReactNode
  metricTooltip?: string
  isPending: boolean
  tooltip?: string
  className?: string
  isActive?: boolean
  children?: React.ReactNode
  onMouseEnter?: () => void
  onFocus?: () => void
} & OnClickOrLinkOrNothing

export const MetricCard = ({
  title,
  currentValue,
  deltaPercentage,
  deltaTooltip,
  metric,
  metricTooltip,
  isPending,
  tooltip,
  className,
  isActive = false,
  children,
  onMouseEnter,
  onFocus,
  ...props
}: Props) => {
  const hasAction = "onClick" in props || "navigate" in props

  const bottomComponent = useMemo(() => {
    if (isPending) {
      return (
        <div className="flex flex-col items-baseline gap-0 text-muted-foreground">
          <div className="flex flex-row items-center">
            <Skeleton className="h-3 my-1 w-16" />
          </div>
        </div>
      )
    }

    if (!isNil(metric)) {
      if (isNil(metricTooltip)) {
        return <p className="w-fit text-left text-muted-foreground text-xs">{metric}</p>
      }

      return (
        <Tooltip>
          <TooltipTrigger className="text-left">
            <p className="text-muted-foreground text-xs">{metric}</p>
          </TooltipTrigger>
          <TooltipContent>{metricTooltip}</TooltipContent>
        </Tooltip>
      )
    }

    if (currentValue !== "-" && !isNil(deltaPercentage)) {
      let Icon = IconCaretRightFilled // = 0
      if (deltaPercentage > 0) Icon = IconCaretUpFilled
      if (deltaPercentage < 0) Icon = IconCaretDownFilled

      return (
        <Tooltip>
          <TooltipTrigger className="text-left">
            <div className="flex flex-row items-center items-baseline gap-0 text-muted-foreground">
              <Icon className="size-5" />
              <p className="text-xs">{formatPercent({ amount: deltaPercentage, fix: 0 }, true)}</p>
            </div>
          </TooltipTrigger>
          <TooltipContent>{deltaTooltip}</TooltipContent>
        </Tooltip>
      )
    }
  }, [isPending, currentValue, deltaPercentage, deltaTooltip, metric, metricTooltip])

  const content = (
    <>
      <div className="flex flex-col items-top min-w-0 gap-2 justify-start flex-1">
        {tooltip && (
          <Tooltip>
            <TooltipTrigger slick className="flex text-sm font-medium justify-start text-left">
              {title}
            </TooltipTrigger>
            <TooltipContent className="max-w-90">{tooltip}</TooltipContent>
          </Tooltip>
        )}
        {!tooltip && (
          <div className="flex text-sm font-medium justify-start text-left">{title}</div>
        )}
        {bottomComponent}
      </div>
      <div className="flex text-2xl font-mono self-start justify-end leading-none whitespace-nowrap flex-shrink-0 pt-1">
        {isPending ? (
          <div className="bg-muted rounded h-8 opacity-50">
            <Skeleton className="h-8 w-16" />
          </div>
        ) : (
          !children && currentValue
        )}
      </div>
      <div
        className={cn("absolute right-4 top-1 z-10 pointer-events-none", isPending && "opacity-50")}
      >
        {children}
      </div>
    </>
  )

  const cardClassName = cn(
    "relative overflow-hidden flex flex-row min-h-20 w-full text-center h-full w-full justify-between border-1 shadow-none px-4 py-3 gap-3",
    isPending && hasAction && "pointer-events-none",
    !isPending && isActive && "bg-accent",
    !isPending &&
      hasAction &&
      "hover:bg-accent active:bg-accent focus:bg-accent cursor-pointer select-none",
    className,
  )

  if ("navigate" in props) {
    return (
      <Link
        {...props.navigate}
        className={cn(
          "rounded-lg border border-border/80 bg-card text-card-foreground shadow-xs relative",
          cardClassName,
        )}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
      >
        {content}
      </Link>
    )
  }

  return (
    <Card
      className={cardClassName}
      onClick={!isPending && "onClick" in props ? props.onClick : undefined}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
    >
      {content}
    </Card>
  )
}
